import API from '@/store/apiUtils'
import { GENERIC_SERVER_ERROR } from '@/constants'

const futureEnrolmentModule = {
  namespaced: true,
  state: {
    registrationOfInterest: [],
    loading: false,
    error: null
  },
  getters: {
    registrationOfInterestList: (state) => {
      try {
        const result = state.registrationOfInterest.map((item) => ({
          id: item.applicationId,
          received: item.dateReceived,
          contactName: `${item.application.parentCarer.parentCarerFamilyName}, ${item.application.parentCarer.parentCarerGivenName}`,
          contactNumber: item.application.parentCarer.parentCarerContactNo,
          contactEmail: item.application.parentCarer.parentCarerEmail,
          studentName: `${item.application.student.familyName}, ${item.application.student.firstName}`,
          studentDOB: item.application.student.dateOfBirth,
          scholasticYear: item.application.applicationDetails.scholasticYear,
          startYear: item.application.applicationDetails.intendedStartYear,
          type: item.isInArea ? 'Local' : 'Non-local',
          contactAddress: `${item.application.parentCarer.residentialAddress.addressLine1}, ${item.application.parentCarer.residentialAddress.suburbName} ${item.application.parentCarer.residentialAddress.stateCode} ${item.application.parentCarer.residentialAddress.postCode}`,
          emailSent: item.enrolmentEmailSent ?? false,
          referralSource: item.application.applicationDetails.referralSource,
          referralSourceOther:
            item.application.applicationDetails.referralSourceOther,
          notes: item.application.applicationDetails.noteText
        }))
        return result
      } catch (error) {
        return []
      }
    },
    registrationOfInterestLoading: (state) => state.loading,
    registrationOfInterestError: (state) => state.error
  },
  mutations: {
    setRegistrationOfInterest(state, data) {
      state.registrationOfInterest = data
    },
    setLoading(state, value) {
      state.loading = value
    },
    setError(state, error) {
      state.error = error
    },
    updateEmailSentStatus(state, updates) {
      // updates is an array of { applicationId, enrolmentEmailSent }
      updates.forEach((update) => {
        const item = state.registrationOfInterest.find(
          (i) => i.applicationId === update.applicationId
        )
        if (item) {
          item.enrolmentEmailSent = update.enrolmentEmailSent
        }
      })
    },
    updateNotes(state, { applicationId, notes }) {
      const itemIndex = state.registrationOfInterest.findIndex(
        (i) => i.applicationId === applicationId
      )
      if (itemIndex !== -1) {
        // Reassign to trigger Vue reactivity
        const updatedItem = {
          ...state.registrationOfInterest[itemIndex],
          application: {
            ...state.registrationOfInterest[itemIndex].application,
            applicationDetails: {
              ...state.registrationOfInterest[itemIndex].application
                .applicationDetails,
              noteText: notes
            }
          }
        }
        state.registrationOfInterest.splice(itemIndex, 1, updatedItem)
      }
    },
    deleteRegistrationOfInterest(state, applicationId) {
      const itemIndex = state.registrationOfInterest.findIndex(
        (i) => i.applicationId === applicationId
      )
      if (itemIndex !== -1) {
        state.registrationOfInterest.splice(itemIndex, 1)
      }
    }
  },
  actions: {
    async fetchRegistrationOfInterest({ commit, dispatch }, { schoolId }) {
      commit('setLoading', true)
      commit('setError', null)

      try {
        const apiUrl = `${process.env.VUE_APP_API_PI}/v1/regofint/applications/${schoolId}`
        const customHeaders = {
          schoolcode: schoolId
        }
        const response = await API.get(apiUrl, false, customHeaders)
        const dataToCommit = response.data.body.registrationOfInterest

        commit('setRegistrationOfInterest', dataToCommit)
        commit('setLoading', false)

        return response
      } catch (error) {
        const errorMessage = GENERIC_SERVER_ERROR
        commit('setError', errorMessage)
        commit('setLoading', false)

        dispatch(
          'showMessageBox',
          {
            icon: 'priority_high',
            html: `<h2>Error: Unable to complete request</h2>${errorMessage}`
          },
          { root: true }
        )
        return Promise.reject(error)
      }
    },
    async sendEmailsForApplications(
      { commit },
      { schoolCode, applicationIds }
    ) {
      commit('setLoading', true)
      commit('setError', null)

      try {
        const apiUrl = `${process.env.VUE_APP_API_PI}/v1/regofint/emails/${schoolCode}`
        const payload = {
          registrationOfInterests: applicationIds.map((id) => ({
            applicationId: id
          }))
        }
        const customHeaders = {
          schoolcode: schoolCode
        }
        const response = await API.post(
          apiUrl,
          payload,
          false,
          customHeaders,
          true
        )

        // Update the emailSent status based on response
        if (response.data.body && response.data.body.results) {
          const updates = response.data.body.results.map((result) => ({
            applicationId: result.applicationId,
            enrolmentEmailSent: result.enrolmentEmailSent
          }))
          commit('updateEmailSentStatus', updates)
        }

        commit('setLoading', false)
        return response
      } catch (error) {
        commit('setLoading', false)
        throw error
      }
    },
    async updateNotesForApplication(
      { commit },
      { schoolCode, applicationId, notes }
    ) {
      // API is not yet implemented, so this is a placeholder for the future implementation
      commit('setLoading', true)
      commit('setError', null)

      try {
        const apiUrl = `${process.env.VUE_APP_API_PI}/v1/regofint/applications/${schoolCode}/${applicationId}`
        const payload = {
          noteText: notes
        }
        const customHeaders = {
          schoolcode: schoolCode
        }
        const response = await API.put(
          apiUrl,
          payload,
          false,
          customHeaders,
          true
        )

        // if API call is successful, update the notes in the state
        commit('updateNotes', { applicationId, notes })

        commit('setLoading', false)
        return response
      } catch (error) {
        commit('setLoading', false)
        throw error
      }
    },
    async deleteRegistrationOfInterest(
      { commit },
      { schoolCode, applicationId }
    ) {
      commit('setLoading', true)
      commit('setError', null)

      try {
        const apiUrl = `${process.env.VUE_APP_API_PI}/v1/regofint/applications/${schoolCode}`
        const payload = {
          registrationOfInterests: [
            {
              applicationId: applicationId
            }
          ]
        }
        const customHeaders = {
          schoolcode: schoolCode
        }
        const response = await API.delete(
          apiUrl,
          payload,
          false,
          customHeaders,
          true
        )

        // if API call is successful, remove the registration from state
        commit('deleteRegistrationOfInterest', applicationId)

        commit('setLoading', false)
        return response
      } catch (error) {
        commit('setLoading', false)
        throw error
      }
    }
  }
}

export default futureEnrolmentModule
